import React, { useEffect, useState } from "react";
import { Celestials } from "../types";
import { toRad } from "../Utils";
import InfoCard from "../../InfoCard";
import {
  CELESTIAL_OBJECTS_SETTING,
  CelestialObjectComponent,
} from "./CelestialCircle";

interface StarData {
  id: number;
  ecl_lon: number;
  ecl_lat: number;
  mag: number;
}

interface LineData {
  source: number;
  target: number;
}

interface Bounds {
  lat_min: number;
  lat_max: number;
}

interface ZodiacMapData {
  stars: StarData[];
  extra_stars: StarData[];
  lines: LineData[];
  bounds: Bounds;
}

interface Props {
  rStart: number;
  rEnd: number;
  canvas: number[];
  fg: string;
  bg: string;
  celestials: Celestials;
}

const PLANET_ORDER = [
  "pluto",
  "neptune",
  "uranus",
  "saturn",
  "mercury",
  "mars",
  "jupiter",
  "venus",
  "moon",
  "sun",
];

const PLANET_COLORS: Record<string, string> = {
  sun: "#FDB813",
  moon: "#C0C0C0",
  mercury: "#B5B5B5",
  venus: "#E6C229",
  mars: "#E74C3C",
  jupiter: "#D4A574",
  saturn: "#E8D191",
  uranus: "#7EC8E3",
  neptune: "#3F54BA",
  pluto: "#9B8B7B",
};

// Module-level promise cache — prevents duplicate fetches in StrictMode
let zodiacMapPromise: Promise<ZodiacMapData> | null = null;

const loadZodiacMap = (): Promise<ZodiacMapData> => {
  if (!zodiacMapPromise) {
    zodiacMapPromise = fetch("/data/zodiac_map.json").then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    });
  }
  return zodiacMapPromise;
};

const eclipticToRing = (
  eclLonDeg: number,
  eclLatDeg: number,
  rStart: number,
  rEnd: number,
  cx: number,
  cy: number,
  latMin: number,
  latMax: number,
): [number, number] => {
  const lat = Math.max(latMin, Math.min(latMax, eclLatDeg));
  const latRange = latMax - latMin;
  const t = latRange > 0 ? (lat - latMin) / latRange : 0.5;
  const r = rStart + t * (rEnd - rStart);
  const angle = toRad(eclLonDeg);
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
};

// ─────────────────────────────────────────────────────────────
// Memoized star map — this is the heavy part of ZodiacRing
// (hundreds of stars + constellation lines). It only
// re-renders when the data or dimensions change, NOT on
// every animation frame when planet positions update.
// ─────────────────────────────────────────────────────────────
interface StarMapProps {
  skyData: ZodiacMapData;
  starById: Record<number, StarData>;
  rStart: number;
  rEnd: number;
  cx: number;
  cy: number;
  latMin: number;
  latMax: number;
  eclipticR: number;
  fg: string;
}

const ZodiacStarMap: React.FC<StarMapProps> = React.memo(
  ({
    skyData,
    starById,
    rStart,
    rEnd,
    cx,
    cy,
    latMin,
    latMax,
    eclipticR,
    fg,
  }) => {
    return (
      <>
        {/* Ecliptic dotted line */}
        <circle
          cx={cx}
          cy={cy}
          r={eclipticR}
          fill="none"
          stroke={fg}
          strokeOpacity={0.2}
          strokeWidth={0.8}
          strokeDasharray="4 6"
        />

        {/* Constellation lines */}
        {skyData.lines.map((line, idx) => {
          const src = starById[line.source];
          const tgt = starById[line.target];
          if (!src || !tgt) return null;
          const [x1_s, y1_s] = eclipticToRing(
            src.ecl_lon,
            src.ecl_lat,
            rStart,
            rEnd,
            cx,
            cy,
            latMin,
            latMax,
          );
          const [x2_s, y2_s] = eclipticToRing(
            tgt.ecl_lon,
            tgt.ecl_lat,
            rStart,
            rEnd,
            cx,
            cy,
            latMin,
            latMax,
          );
          return (
            <line
              key={`zr-line-${idx}`}
              x1={x1_s}
              y1={y1_s}
              x2={x2_s}
              y2={y2_s}
              stroke={fg}
              strokeOpacity={0.3}
              strokeWidth={0.5}
            />
          );
        })}

        {/* Constellation stars */}
        {skyData.stars.map((star) => {
          const [sx, sy] = eclipticToRing(
            star.ecl_lon,
            star.ecl_lat,
            rStart,
            rEnd,
            cx,
            cy,
            latMin,
            latMax,
          );
          const radius = Math.max(0.5, 4.0 - star.mag * 0.5);
          return (
            <circle
              key={`zr-star-${star.id}`}
              cx={sx}
              cy={sy}
              r={radius}
              fill={fg}
              fillOpacity={Math.max(0.2, 1.0 - star.mag / 6.0)}
            />
          );
        })}

        {/* Extra bright stars */}
        {skyData.extra_stars.map((star) => {
          const [sx, sy] = eclipticToRing(
            star.ecl_lon,
            star.ecl_lat,
            rStart,
            rEnd,
            cx,
            cy,
            latMin,
            latMax,
          );
          const radius = Math.max(0.5, 3.0 - star.mag * 0.4);
          return (
            <circle
              key={`zr-ext-${star.id}`}
              cx={sx}
              cy={sy}
              r={radius}
              fill={fg}
              fillOpacity={Math.max(0.15, 0.8 - star.mag / 6.0)}
            />
          );
        })}
      </>
    );
  },
);

// ─────────────────────────────────────────────────────────────
// ZodiacRing — now only re-renders the 10 planet dots
// during animation. The heavy star map is memoized above.
// ─────────────────────────────────────────────────────────────
const ZodiacRing: React.FC<Props> = ({
  rStart,
  rEnd,
  canvas,
  fg,
  bg,
  celestials,
}) => {
  const [skyData, setSkyData] = useState<ZodiacMapData>({
    stars: [],
    extra_stars: [],
    lines: [],
    bounds: { lat_min: -20, lat_max: 20 },
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] =
    useState<null | CelestialObjectComponent>(null);

  const celestialByName: Record<string, CelestialObjectComponent> = {};
  for (const obj of CELESTIAL_OBJECTS_SETTING) {
    celestialByName[obj.name] = obj;
  }

  useEffect(() => {
    let cancelled = false;
    loadZodiacMap()
      .then((data: ZodiacMapData) => {
        if (!cancelled) setSkyData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("ZodiacRing: failed to fetch zodiac map:", err);
          setError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return null;

  const cx = canvas[0] / 2;
  const cy = canvas[1] / 2;
  const { lat_min: latMin, lat_max: latMax } = skyData.bounds;

  // Build a lookup from HIP id → star data for line rendering
  const starById: Record<number, StarData> = {};
  for (const s of skyData.stars) starById[s.id] = s;
  for (const s of skyData.extra_stars) starById[s.id] = s;

  // Ecliptic dotted‑line radius (latitude = 0°)
  const [eclX, eclY] = eclipticToRing(
    0,
    0,
    rStart,
    rEnd,
    cx,
    cy,
    latMin,
    latMax,
  );
  const eclipticR = Math.sqrt((eclX - cx) ** 2 + (eclY - cy) ** 2);

  return (
    <g data-testid="zodiac-ring">
      {/* Static star map — memoized, skips re-render during planet animation */}
      <ZodiacStarMap
        skyData={skyData}
        starById={starById}
        rStart={rStart}
        rEnd={rEnd}
        cx={cx}
        cy={cy}
        latMin={latMin}
        latMax={latMax}
        eclipticR={eclipticR}
        fg={fg}
      />

      {/* Planets, Sun, Moon — only these update every animation frame */}
      {PLANET_ORDER.map((name) => {
        const cel = celestials[name];
        if (!cel) return null;
        const color = PLANET_COLORS[name] || fg;
        const latRange = latMax - latMin;
        const clampedLat = Math.max(latMin, Math.min(latMax, cel.ecliptic.lat));
        const t = latRange > 0 ? (clampedLat - latMin) / latRange : 0.5;
        const rBody = rStart + t * (rEnd - rStart);
        const dotR = name === "sun" ? 10 : name === "moon" ? 8 : 6;
        const info = celestialByName[name];
        return (
          <g
            key={`zr-planet-${name}`}
            transform={`rotate(${cel.ecliptic.lon} ${cx} ${cy})`}
            cursor={info ? "pointer" : undefined}
            onClick={info ? () => setSelectedObject(info) : undefined}
          >
            <circle cx={cx + rBody} cy={cy} r={dotR} fill={color} />
          </g>
        );
      })}
      {selectedObject && (
        <InfoCard
          open={!!selectedObject}
          onClose={() => setSelectedObject(null)}
          image={selectedObject.image}
          title={selectedObject.label}
          description={selectedObject.description}
        />
      )}
    </g>
  );
};

export default React.memo(ZodiacRing);
